import React, { useEffect, useMemo, useState } from 'react'

import { DownloadOutlined, PrinterOutlined, FileTextOutlined, RollbackOutlined, SendOutlined, WarningOutlined, LoadingOutlined } from '@ant-design/icons'
import { Alert, Badge, Button, Card, Checkbox, Collapse, Descriptions, Empty, Input, InputNumber, Modal, Progress, Select, Space, Spin, Table, Tag, Typography, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams } from 'react-router-dom'
import ListPageHeader from '@shared/ListPageHeader'
import WaveAttachments from '@shared/picking_waves/WaveAttachments'

import { operatorApiFetch } from '@services/operator/apiService'
import { useBackgroundJob } from '@/components/shared/BackgroundJobManager'

import { getColorForText } from '@/utils/colorTag'
import { formatDateTime } from '@/utils/dateTime'

const TERMINAL_FULFILLMENT_STATUSES = ['shipped', 'canceled', 'terminated']
const isReturnable = (f) => f && !TERMINAL_FULFILLMENT_STATUSES.includes(f.status)

export default function PickingWaveShow() {
  const { id } = useParams()
  const location = useLocation()
  const { t } = useTranslation()
  const { addJob } = useBackgroundJob()
  const [loading, setLoading] = useState(true)
  const [wave, setWave] = useState(null)
  const [selectedFulfillments, setSelectedFulfillments] = useState(new Set())
  const [printModalOpen, setPrintModalOpen] = useState(false)
  const [selectedPickingLists, setSelectedPickingLists] = useState(new Set())
  const [printing, setPrinting] = useState(false)
  // 批次取號 / 下載標籤
  const [requestingTracking, setRequestingTracking] = useState(false)
  const [downloadingLabels, setDownloadingLabels] = useState(false)
  const [downloadingAttachments, setDownloadingAttachments] = useState(false)
  // TO Label 裝箱單
  const [toLabelModalOpen, setToLabelModalOpen] = useState(false)
  const [toLabelLoading, setToLabelLoading] = useState(false)
  const [toLabelNorth, setToLabelNorth] = useState(0)
  const [toLabelSouth, setToLabelSouth] = useState(0)
  // 退回 fulfillment（單筆）
  const [returnModalOpen, setReturnModalOpen] = useState(false)
  const [returnLoading, setReturnLoading] = useState(false)
  const [returnFulfillment, setReturnFulfillment] = useState(null)
  const [returnToteItems, setReturnToteItems] = useState([])
  const [returnPutBackItems, setReturnPutBackItems] = useState([])
  // 批次退回 fulfillment
  const [batchReturnModalOpen, setBatchReturnModalOpen] = useState(false)
  const [batchReturnLoading, setBatchReturnLoading] = useState(false)
  const [batchReturnSubmitting, setBatchReturnSubmitting] = useState(false)
  const [batchReturnEntries, setBatchReturnEntries] = useState([])
  const [batchReturnFetchError, setBatchReturnFetchError] = useState(null)
  // 作廢 wave（abort）— PR 4.1 §17
  const [abortModalOpen, setAbortModalOpen] = useState(false)
  const [abortLoading, setAbortLoading] = useState(false)
  const [abortSubmitting, setAbortSubmitting] = useState(false)
  const [abortEntries, setAbortEntries] = useState([])
  const [abortFetchError, setAbortFetchError] = useState(null)
  const [abortConfirmText, setAbortConfirmText] = useState('')

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await operatorApiFetch(`picking_waves/${id}`)
      const data = await response.json()
      if (data.success) {
        setWave(data.data)
      }
    } catch (error) {
      console.error('Failed to fetch picking wave:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!id) return
    fetchData()
  }, [id])

  // Scroll to anchor (e.g. #fulfillments) once data is rendered.
  // Wave page is async-loaded, so the target element doesn't exist until `wave` is set.
  useEffect(() => {
    if (loading || !wave || !location.hash) return
    const targetId = location.hash.slice(1)
    const el = document.getElementById(targetId)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [loading, wave, location.hash])

  // 顯示全部 FL；action/checkbox 仍由 isReturnable 控制可點性
  const allFulfillments = useMemo(() => wave?.fulfillments || [], [wave])

  // 來自 picking_list 頁面「前往波次頁面退回」link 帶入的 held order IDs，用來高亮對應 FL row
  const highlightOrderIds = useMemo(
    () => new Set(location.state?.highlightOrderIds || []),
    [location.state]
  )

  const returnableIds = useMemo(
    () => allFulfillments.filter(isReturnable).map((f) => f.id),
    [allFulfillments]
  )

  const nonTerminalCount = returnableIds.length

  // 列印揀貨單
  const handlePrintAll = async () => {
    setPrinting(true)
    try {
      const response = await operatorApiFetch(`picking_waves/${id}/print`, { method: 'POST' })
      const data = await response.json()
      if (data.success) {
        const jobIds = data.data.job_ids || []
        jobIds.forEach((jobId, index) => {
          addJob(jobId, 'shared.picking_waves.show.actions.print_picking_lists_progress', {
            nameParams: { current: index + 1, total: jobIds.length }
          })
        })
        message.success(t('background_job.workers.picking_lists.pdf_export.start'))
        fetchData()
      } else {
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (error) {
      console.error('Failed to print:', error)
      message.error(t('common.error.operation_failed'))
    } finally {
      setPrinting(false)
    }
  }

  const handlePrintSelected = async () => {
    if (selectedPickingLists.size === 0) return
    setPrinting(true)
    try {
      const plIds = Array.from(selectedPickingLists)
      const jobIds = []
      for (const plId of plIds) {
        const response = await operatorApiFetch(`picking_lists/${plId}/generate_pdf`, { method: 'POST' })
        const data = await response.json()
        if (data.success && data.data?.job_id) {
          jobIds.push(data.data.job_id)
        }
      }
      jobIds.forEach((jobId, index) => {
        addJob(jobId, 'shared.picking_waves.show.actions.print_picking_lists_progress', {
          nameParams: { current: index + 1, total: jobIds.length }
        })
      })
      message.success(t('background_job.workers.picking_lists.pdf_export.start'))
      setPrintModalOpen(false)
      fetchData()
    } catch (error) {
      console.error('Failed to print:', error)
      message.error(t('common.error.operation_failed'))
    } finally {
      setPrinting(false)
    }
  }

  const handlePrintPickingLists = () => {
    const pickingLists = wave?.picking_lists || []
    if (pickingLists.length <= 1) {
      handlePrintAll()
    } else {
      setSelectedPickingLists(new Set(pickingLists.map(pl => pl.id)))
      setPrintModalOpen(true)
    }
  }

  const handlePrintFulfillments = async () => {
    try {
      const response = await operatorApiFetch(`picking_waves/${id}/generate_fulfillment_pdf`, { method: 'POST' })
      const data = await response.json()
      if (data.success) {
        addJob(data.data.job_id, 'shared.picking_waves.show.actions.print_fulfillments')
        message.success(t('background_job.workers.generate_fulfillment_pdf.start'))
      } else {
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (error) {
      console.error('Failed to generate fulfillment PDF:', error)
      message.error(t('common.error.operation_failed'))
    }
  }

  // 批次取號（物流帳號由後端 ShippingAccountResolver 自動選擇）
  const handleBatchRequestTrackingNumbers = async () => {
    if (requestingTracking) return
    setRequestingTracking(true)
    try {
      const response = await operatorApiFetch(`picking_waves/${id}/batch_request_tracking_numbers`, {
        method: 'POST',
      })
      const data = await response.json()
      if (data.success) {
        addJob(data.data.job_id, 'shared.picking_lists.detail.actions.batch_request_tracking_numbers', {
          onComplete: () => fetchData(),
          onError: (jobData, errorMsg, interpolations) => {
            fetchData()
            const isI18nKey = errorMsg?.includes('.') && !errorMsg?.includes(' ')
            message.error(isI18nKey ? t(errorMsg, interpolations || {}) : (errorMsg || t('common.error.operation_failed')))
          }
        })
        message.success(t('background_job.workers.picking_waves.batch_request_tracking_numbers.start'))
      } else {
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (error) {
      console.error('Failed to batch request tracking numbers:', error)
      message.error(t('common.error.operation_failed'))
    } finally {
      setRequestingTracking(false)
    }
  }

  // 下載托運單 (ZIP)
  const handleBatchDownloadLabels = async () => {
    if (downloadingLabels) return
    setDownloadingLabels(true)
    try {
      const response = await operatorApiFetch(`picking_waves/${id}/batch_download_labels`, { method: 'POST' })
      const data = await response.json()
      if (data.success) {
        addJob(data.data.job_id, 'shared.picking_lists.detail.actions.batch_download_labels')
        message.success(t('background_job.workers.picking_lists.batch_label_download.start'))
      } else {
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (error) {
      console.error('Failed to batch download labels:', error)
      message.error(t('common.error.operation_failed'))
    } finally {
      setDownloadingLabels(false)
    }
  }

  // 下載本波次所揀訂單的附件 (ZIP)
  const handleDownloadAllAttachments = async () => {
    if (downloadingAttachments) return
    setDownloadingAttachments(true)
    try {
      const response = await operatorApiFetch(`picking_waves/${id}/download_attachments`, { method: 'POST' })
      const data = await response.json()
      if (data.success) {
        addJob(data.data.job_id, 'shared.picking_waves.show.attachments.download_attachments')
        message.success(t('background_job.workers.picking_lists.attachments_download.start'))
      } else {
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (error) {
      console.error('Failed to download attachments:', error)
      message.error(t('common.error.operation_failed'))
    } finally {
      setDownloadingAttachments(false)
    }
  }

  // 下載裝箱單 (TO Label)
  const handleDownloadToLabel = async () => {
    if (toLabelNorth <= 0 && toLabelSouth <= 0) return
    setToLabelLoading(true)
    try {
      const response = await operatorApiFetch(`picking_waves/${id}/download_to_label`, {
        method: 'POST',
        body: JSON.stringify({ north_quantity: toLabelNorth, south_quantity: toLabelSouth }),
        rawResponse: true
      })
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `to_label_${new Date().toISOString().slice(0, 10)}.pdf`
        a.click()
        window.URL.revokeObjectURL(url)
        setToLabelModalOpen(false)
        setToLabelNorth(0)
        setToLabelSouth(0)
        message.success(t('shared.picking_waves.show.to_label.download_success'))
      } else {
        const data = await response.json()
        message.error(data.message || t('common.error.operation_failed'))
      }
    } catch (error) {
      console.error('Failed to download TO label:', error)
      message.error(t('common.error.operation_failed'))
    } finally {
      setToLabelLoading(false)
    }
  }

  // 從 fulfillment_tote_status response 取出需要操作員處理的 items
  const toActionableEntry = (fulfillment, payload) => {
    const allItems = payload?.items || []
    const toteItems = allItems.filter((item) => (item.put_back_required || 0) > 0)
    const putBackItems = toteItems.map((item) => ({
      inventory_id: item.inventory_id,
      picking_list_id: item.picking_list_id,
      quantity: item.put_back_required,
      shelf_id: item.source_shelves?.[0]?.shelf_id || null,
    }))
    return { fulfillment, toteItems, putBackItems }
  }

  // 退回 fulfillment — 查詢單筆 FL 的 tote 狀態（可重用：打開 Modal & 並行 refetch）
  // §8.5: concurrency 錯誤時自動重 fetch，不重置操作員已填入的儲位（若 source 仍相同）
  const refetchSingleFulfillmentToteStatus = async (fulfillment) => {
    const response = await operatorApiFetch(
      `picking_waves/${id}/fulfillment_tote_status?fulfillment_id=${fulfillment.id}`
    )
    const data = await response.json()
    if (!data.success) {
      throw new Error(data.message || 'fetch_failed')
    }
    return toActionableEntry(fulfillment, data.data)
  }

  // 退回 fulfillment — 打開 Modal（查詢 tote 狀態）
  const handleReturnClick = async (fulfillment) => {
    setReturnFulfillment(fulfillment)
    setReturnLoading(true)
    setReturnModalOpen(true)

    try {
      const { toteItems, putBackItems } = await refetchSingleFulfillmentToteStatus(fulfillment)
      setReturnToteItems(toteItems)
      setReturnPutBackItems(putBackItems)
    } catch (error) {
      console.error('Failed to fetch tote status:', error)
      message.error(t('common.error.operation_failed'))
      setReturnModalOpen(false)
    } finally {
      setReturnLoading(false)
    }
  }

  // 退回 fulfillment — 確認執行（單筆）
  const handleReturnConfirm = async () => {
    if (!returnFulfillment) return

    // 驗證：如果有 tote 庫存，每個 item 必須選擇儲位
    const itemsToReturn = returnPutBackItems.filter(item => item.quantity > 0)
    if (itemsToReturn.length > 0) {
      const missingShelf = itemsToReturn.some(item => !item.shelf_id)
      if (missingShelf) {
        message.error(t('shared.picking_waves.show.return_modal.missing_shelf'))
        return
      }
    }

    setReturnLoading(true)
    try {
      const response = await operatorApiFetch(`picking_waves/${id}/return_fulfillment`, {
        method: 'POST',
        body: JSON.stringify({
          fulfillment_id: returnFulfillment.id,
          put_back_items: itemsToReturn,
        })
      })
      const result = await response.json()
      if (result.success) {
        message.success(t('shared.picking_waves.show.return_modal.success'))
        // §6.3 / §8.5 Warnings：動作成功但帶有附帶狀況（例如 auto-reopen 被擋）
        const warnings = Array.isArray(result.data?.warnings) ? result.data.warnings : []
        warnings.forEach((w) => {
          const key = w.message_key || (w.code && `errors.${w.code}`)
          message.warning(key ? t(key, w.code) : (w.code || ''))
        })
        setReturnModalOpen(false)
        setReturnFulfillment(null)
        setReturnToteItems([])
        setReturnPutBackItems([])
        fetchData()
      } else if (result.message === 'insufficient_inventory') {
        // §8.5 Concurrency：tote 庫存已被其他操作變動，自動重 fetch，不關閉對話框
        message.warning(t('errors.insufficient_inventory_refetching'))
        try {
          const { toteItems, putBackItems } = await refetchSingleFulfillmentToteStatus(returnFulfillment)
          setReturnToteItems(toteItems)
          setReturnPutBackItems(putBackItems)
        } catch (e) {
          console.error('Failed to refetch tote status after concurrency error:', e)
          message.error(t('common.error.operation_failed'))
        }
      } else {
        message.error(result.errors || result.message || t('common.error.operation_failed'))
      }
    } catch (error) {
      console.error('Failed to return fulfillment:', error)
      message.error(t('common.error.operation_failed'))
    } finally {
      setReturnLoading(false)
    }
  }

  // 更新單筆退回項目的儲位選擇
  const updateReturnShelf = (index, shelfId) => {
    setReturnPutBackItems(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], shelf_id: shelfId }
      return updated
    })
  }

  // 開啟批次退回 Modal — 同時查詢所有已選 FL 的 tote 狀態
  const handleBatchReturnClick = async () => {
    const selected = (wave?.fulfillments || []).filter((f) => selectedFulfillments.has(f.id) && isReturnable(f))
    if (selected.length === 0) return

    setBatchReturnModalOpen(true)
    setBatchReturnLoading(true)
    setBatchReturnFetchError(null)
    setBatchReturnEntries([])

    try {
      const results = await Promise.all(
        selected.map(async (f) => {
          const response = await operatorApiFetch(
            `picking_waves/${id}/fulfillment_tote_status?fulfillment_id=${f.id}`
          )
          const data = await response.json()
          if (!data.success) {
            throw new Error(data.message || f.number || f.id)
          }
          return toActionableEntry(f, data.data)
        })
      )
      setBatchReturnEntries(results)
    } catch (error) {
      console.error('Failed to fetch batch tote status:', error)
      setBatchReturnFetchError(error.message || t('shared.picking_waves.show.batch_return_modal.fetch_error'))
    } finally {
      setBatchReturnLoading(false)
    }
  }

  // 更新批次退回 Modal 中某 FL 某 item 的儲位
  const updateBatchReturnShelf = (fulfillmentId, itemIndex, shelfId) => {
    setBatchReturnEntries((prev) =>
      prev.map((entry) => {
        if (entry.fulfillment.id !== fulfillmentId) return entry
        const putBackItems = entry.putBackItems.map((pb, idx) =>
          idx === itemIndex ? { ...pb, shelf_id: shelfId } : pb
        )
        return { ...entry, putBackItems }
      })
    )
  }

  // 確認批次退回
  const handleBatchReturnConfirm = async () => {
    // 驗證：每個 FL 中需要 put_back 的 item 必須都選了儲位
    const hasMissingShelf = batchReturnEntries.some((entry) =>
      entry.putBackItems.some((pb) => pb.quantity > 0 && !pb.shelf_id)
    )
    if (hasMissingShelf) {
      message.error(t('shared.picking_waves.show.batch_return_modal.missing_shelf'))
      return
    }

    const body = {
      items: batchReturnEntries.map((entry) => ({
        fulfillment_id: entry.fulfillment.id,
        put_back_items: entry.putBackItems.filter((pb) => pb.quantity > 0),
      })),
    }

    setBatchReturnSubmitting(true)
    try {
      const response = await operatorApiFetch(`picking_waves/${id}/batch_return_fulfillments`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      const result = await response.json()
      if (result.success) {
        const count = result.data?.returned_fulfillment_ids?.length || batchReturnEntries.length
        message.success(t('shared.picking_waves.show.batch_return_modal.success', { count }))
        // §6.3 / §8.5 Warnings
        const warnings = Array.isArray(result.data?.warnings) ? result.data.warnings : []
        warnings.forEach((w) => {
          const key = w.message_key || (w.code && `errors.${w.code}`)
          message.warning(key ? t(key, w.code) : (w.code || ''))
        })
        setBatchReturnModalOpen(false)
        setBatchReturnEntries([])
        setBatchReturnFetchError(null)
        setSelectedFulfillments(new Set())
        fetchData()
      } else {
        // TODO §8.5 Concurrency：批次 insufficient_inventory 時應該 refetch 所有 FL
        // 的 tote 狀態並顯示 diff；目前僅 surfacing 錯誤，不自動 refetch。
        if (result.message === 'insufficient_inventory') {
          message.warning(t('errors.insufficient_inventory_refetching'))
        } else {
          message.error(result.errors || result.message || t('common.error.operation_failed'))
        }
      }
    } catch (error) {
      console.error('Failed to batch return fulfillments:', error)
      message.error(t('common.error.operation_failed'))
    } finally {
      setBatchReturnSubmitting(false)
    }
  }

  const closeBatchReturnModal = () => {
    if (batchReturnSubmitting) return
    setBatchReturnModalOpen(false)
    setBatchReturnEntries([])
    setBatchReturnFetchError(null)
  }

  // 作廢 Wave（§17）— 開啟 Modal 並查詢所有非 terminal FL 的 tote 狀態
  const handleAbortClick = async () => {
    const nonTerminal = (wave?.fulfillments || []).filter(isReturnable)

    setAbortModalOpen(true)
    setAbortLoading(true)
    setAbortFetchError(null)
    setAbortEntries([])
    setAbortConfirmText('')

    try {
      const results = await Promise.all(
        nonTerminal.map(async (f) => {
          const response = await operatorApiFetch(
            `picking_waves/${id}/fulfillment_tote_status?fulfillment_id=${f.id}`
          )
          const data = await response.json()
          if (!data.success) {
            throw new Error(data.message || f.number || f.id)
          }
          return toActionableEntry(f, data.data)
        })
      )
      setAbortEntries(results)
    } catch (error) {
      console.error('Failed to fetch abort tote status:', error)
      setAbortFetchError(error.message || t('shared.picking_waves.show.abort_modal.fetch_error'))
    } finally {
      setAbortLoading(false)
    }
  }

  const updateAbortShelf = (fulfillmentId, itemIndex, shelfId) => {
    setAbortEntries((prev) =>
      prev.map((entry) => {
        if (entry.fulfillment.id !== fulfillmentId) return entry
        const putBackItems = entry.putBackItems.map((pb, idx) =>
          idx === itemIndex ? { ...pb, shelf_id: shelfId } : pb
        )
        return { ...entry, putBackItems }
      })
    )
  }

  const handleAbortConfirm = async () => {
    const hasMissingShelf = abortEntries.some((entry) =>
      entry.putBackItems.some((pb) => pb.quantity > 0 && !pb.shelf_id)
    )
    if (hasMissingShelf) {
      message.error(t('shared.picking_waves.show.abort_modal.missing_shelf'))
      return
    }

    const body = {
      items: abortEntries.map((entry) => ({
        fulfillment_id: entry.fulfillment.id,
        put_back_items: entry.putBackItems.filter((pb) => pb.quantity > 0),
      })),
    }

    setAbortSubmitting(true)
    try {
      const response = await operatorApiFetch(`picking_waves/${id}/abort`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      const result = await response.json()
      if (result.success) {
        const count = result.data?.returned_fulfillment_ids?.length || abortEntries.length
        message.success(t('shared.picking_waves.show.abort_modal.success', { count }))
        // §6.3 / §8.5 Warnings
        const warnings = Array.isArray(result.data?.warnings) ? result.data.warnings : []
        warnings.forEach((w) => {
          const key = w.message_key || (w.code && `errors.${w.code}`)
          message.warning(key ? t(key, w.code) : (w.code || ''))
        })
        setAbortModalOpen(false)
        setAbortEntries([])
        setAbortConfirmText('')
        fetchData()
      } else {
        // TODO §8.5 Concurrency：abort wave insufficient_inventory 時應該 refetch
        // 所有 FL 的 tote 狀態並顯示 diff；目前僅 surfacing 錯誤，不自動 refetch。
        if (result.message === 'insufficient_inventory') {
          message.warning(t('errors.insufficient_inventory_refetching'))
        } else {
          message.error(result.errors || result.message || t('common.error.operation_failed'))
        }
      }
    } catch (error) {
      console.error('Failed to abort wave:', error)
      message.error(t('common.error.operation_failed'))
    } finally {
      setAbortSubmitting(false)
    }
  }

  const closeAbortModal = () => {
    if (abortSubmitting) return
    setAbortModalOpen(false)
    setAbortEntries([])
    setAbortFetchError(null)
    setAbortConfirmText('')
  }

  const abortConfirmed = !!wave && abortConfirmText.trim() === (wave?.name || '').trim() && abortConfirmText.trim().length > 0

  // §7.2 警示：所有 PL 都已結束，但仍有未處置（非 terminal）的 FL
  // 非終結 FL 狀態集合：allocated / pending / processing / out_of_stock / partial_allocated
  const PENDING_FL_STATUSES = ['allocated', 'pending', 'processing', 'out_of_stock', 'partial_allocated']
  const FINISHED_PL_STATUSES = ['done', 'terminated']
  const hasFinishedPlsWithPendingFls = (() => {
    const pls = wave?.picking_lists || []
    const fls = wave?.fulfillments || []
    if (pls.length === 0) return false
    const allPlsFinished = pls.every((pl) => FINISHED_PL_STATUSES.includes(pl.status))
    const anyPendingFl = fls.some((f) => PENDING_FL_STATUSES.includes(f.status))
    // wave.closable 為 happy end，不重複警示
    return allPlsFinished && anyPendingFl && !wave?.closable
  })()

  const waveProgress = (() => {
    const pls = wave?.picking_lists || []
    // terminated PL 已脫離正常流程（剩餘量被刻意放棄），不應計入「整批揀貨進度」分母，
    // 否則只要任一 PL 被中止、整體百分比就永遠到不了 100%。
    const activePls = pls.filter((pl) => pl.status !== 'terminated')
    const totalQty = activePls.reduce((s, pl) => s + (pl.total_quantity || 0), 0)
    const pickedQty = activePls.reduce((s, pl) => s + (pl.picked_quantity || 0), 0)
    const rawPercent = totalQty > 0 ? Math.round((pickedQty / totalQty) * 100) : 0
    const percent = Math.min(100, rawPercent) // 過撿（picked > total）防護
    const buckets = pls.reduce(
      (acc, pl) => {
        if (pl.status === 'done') acc.done += 1
        else if (pl.status === 'terminated') acc.terminated += 1
        else if (pl.status === 'processing') acc.processing += 1
        return acc
      },
      { done: 0, processing: 0, terminated: 0 }
    )
    return { totalQty, pickedQty, percent, buckets }
  })()

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready': return 'blue'
      case 'processing': return 'orange'
      case 'done': return 'green'
      case 'terminated': return 'volcano'
      default: return 'default'
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!wave) {
    return <div>Picking Wave not found</div>
  }

  const selectedReturnableCount = Array.from(selectedFulfillments).filter((fId) => {
    const f = wave.fulfillments?.find((x) => x.id === fId)
    return isReturnable(f)
  }).length

  const allReturnableSelected =
    returnableIds.length > 0 &&
    returnableIds.every((fId) => selectedFulfillments.has(fId))
  const someReturnableSelected =
    returnableIds.some((fId) => selectedFulfillments.has(fId)) && !allReturnableSelected

  const fulfillmentColumns = [
    ...(returnableIds.length > 0 ? [{
      title: (
        <Checkbox
          checked={allReturnableSelected}
          indeterminate={someReturnableSelected}
          onChange={(e) => {
            if (e.target.checked) {
              const next = new Set(selectedFulfillments)
              returnableIds.forEach((fId) => next.add(fId))
              setSelectedFulfillments(next)
            } else {
              const next = new Set(selectedFulfillments)
              returnableIds.forEach((fId) => next.delete(fId))
              setSelectedFulfillments(next)
            }
          }}
        />
      ),
      key: 'selection',
      width: 50,
      render: (_, record) => {
        if (!isReturnable(record)) return null
        return (
          <Checkbox
            checked={selectedFulfillments.has(record.id)}
            onChange={() => {
              const newSelected = new Set(selectedFulfillments)
              if (newSelected.has(record.id)) {
                newSelected.delete(record.id)
              } else {
                newSelected.add(record.id)
              }
              setSelectedFulfillments(newSelected)
            }}
          />
        )
      },
    }] : []),
    {
      title: t('models.fulfillment.slot_number'),
      dataIndex: 'slot_number',
      key: 'slot_number',
      width: 80,
      sorter: (a, b) => (a.slot_number || 0) - (b.slot_number || 0),
      defaultSortOrder: 'ascend',
    },
    {
      title: t('models.fulfillment.number'),
      dataIndex: 'number',
      key: 'fulfillment_number',
      render: (number, record) => (
        <Link to={`/fulfillments/${record.id}`}>{number || '-'}</Link>
      ),
    },
    {
      title: t('models.order.order_number'),
      dataIndex: ['order', 'name'],
      key: 'order_name',
      render: (text, record) => record.order ? (
        <Link to={`/orders/${record.order.id}`}>{record.order.name}</Link>
      ) : '-',
    },
    {
      title: t('models.fulfillment.status'),
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      render: (status) => {
        const colorMap = {
          pending: 'default',
          allocated: 'blue',
          partial_allocated: 'orange',
          out_of_stock: 'red',
          processing: 'orange',
          shipped: 'green',
          canceled: 'volcano',
          terminated: 'volcano',
        }
        return (
          <Tag color={colorMap[status] || 'default'}>
            {t(`enums.fulfillment.status.${status}`)}
          </Tag>
        )
      },
    },
    {
      title: t('models.shipment.label_status'),
      dataIndex: 'label_status',
      key: 'label_status',
      sorter: (a, b) => (a.label_status || '').localeCompare(b.label_status || ''),
      render: (ls) => {
        if (!ls) return '-'
        const labelColorMap = {
          generated: 'blue',
          error: 'red',
          processing: 'gold',
          pending: 'default',
        }
        return (
          <Tag color={labelColorMap[ls] || 'default'} icon={ls === 'processing' ? <LoadingOutlined spin /> : null}>
            {t(`enums.shipment.label_status.${ls}`)}
          </Tag>
        )
      },
    },
    {
      title: t('models.order.sales_channel'),
      dataIndex: ['order', 'sales_channel'],
      key: 'sales_channel',
      render: (salesChannel) => salesChannel ? (
        <Tag color={getColorForText(salesChannel.name)}>{salesChannel.name}</Tag>
      ) : '-',
    },
    {
      title: t('models.order.receiver_name'),
      dataIndex: ['order', 'receiver_name'],
      key: 'receiver_name',
    },
    {
      title: t('models.fulfillment.items_count'),
      dataIndex: 'items',
      key: 'items_count',
      render: (items) => items?.length || 0,
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => {
        if (!isReturnable(record)) {
          return null
        }
        const isHighlighted = record.order && highlightOrderIds.has(record.order.id)
        return (
          <Button
            size="small"
            icon={<RollbackOutlined />}
            onClick={() => handleReturnClick(record)}
            {...(isHighlighted ? { type: 'primary', danger: true } : {})}
          >
            {t('shared.picking_waves.show.actions.return')}
          </Button>
        )
      },
    },
  ]

  return (
    <div>
      <ListPageHeader
        items={[
          { label: t('operator.menu.picking.title') },
          { label: t('operator.menu.picking_lists.list'), path: '/picking_lists' },
          { label: wave.name },
        ]}
        actions={
          <Space>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrintPickingLists}
              loading={printing}
            >
              {t('shared.picking_waves.show.actions.print_picking_lists')}
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={handlePrintFulfillments}
            >
              {t('shared.picking_waves.show.actions.print_fulfillments')}
            </Button>

            {wave.label_summary?.actionable_pending > 0 && (
              <Button icon={<SendOutlined />} onClick={handleBatchRequestTrackingNumbers} loading={requestingTracking}>
                {t('shared.picking_lists.detail.actions.batch_request_tracking_numbers')}
              </Button>
            )}

            {wave.label_summary?.generated > 0 && (
              <Button icon={<DownloadOutlined />} onClick={handleBatchDownloadLabels} loading={downloadingLabels}>
                {t('shared.picking_lists.detail.actions.batch_download_labels')}
              </Button>
            )}

            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadAllAttachments}
              loading={downloadingAttachments}
              disabled={(wave.attachments || []).length === 0}
            >
              {t('shared.picking_waves.show.attachments.download_attachments')}
            </Button>

            {wave.has_unpackaged && wave.label_summary?.generated > 0 && (
              <Button icon={<FileTextOutlined />} onClick={() => setToLabelModalOpen(true)}>
                {t('shared.picking_waves.show.to_label.button')}
              </Button>
            )}

            {wave.label_summary?.error > 0 && (
              <Button icon={<SendOutlined />} onClick={handleBatchRequestTrackingNumbers} loading={requestingTracking}>
                {t('shared.picking_lists.detail.actions.retry_tracking_numbers', {
                  count: wave.label_summary?.error
                })}
              </Button>
            )}

            {nonTerminalCount > 0 && (
              <Button
                danger
                icon={<WarningOutlined />}
                onClick={handleAbortClick}
              >
                {t('shared.picking_waves.show.actions.abort_wave')}
              </Button>
            )}
          </Space>
        }
      />

      {/* 基本資訊 */}
      <Card style={{ marginBottom: '16px' }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t('shared.picking_waves.show.wave_name')}>
            <Space>
              <span>{wave.name}</span>
              {wave.closable && (
                <Badge
                  color="green"
                  text={t('shared.picking_waves.show.closable')}
                />
              )}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label={t('models.merchant.name')}>
            {wave.merchant?.name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.picking_waves.show.fulfillments_count')}>
            {wave.fulfillments?.length || 0}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.shipment.label_status')}>
            {wave.label_summary?.total > 0 ? (
              <Space>
                {wave.label_summary?.generated > 0 && (
                  <Tag color="blue">{t('enums.shipment.label_status.generated')}: {wave.label_summary.generated}</Tag>
                )}
                {wave.label_summary?.error > 0 && (
                  <Tag color="red">{t('enums.shipment.label_status.error')}: {wave.label_summary.error}</Tag>
                )}
                {wave.label_summary?.processing > 0 && (
                  <Tag color="gold">{t('enums.shipment.label_status.processing')}: {wave.label_summary.processing}</Tag>
                )}
                {wave.label_summary?.pending > 0 && (
                  <Tag>{t('enums.shipment.label_status.pending')}: {wave.label_summary.pending}</Tag>
                )}
              </Space>
            ) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.created_at')}>
            {formatDateTime(wave.created_at)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* §7.2 警示：PL 全結束但 FL 未處置 */}
      {hasFinishedPlsWithPendingFls && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={t('shared.picking_waves.show.warning_finished_pls_with_pending_fls')}
          style={{ marginBottom: '16px' }}
        />
      )}

      <Card
        title={t('shared.picking_waves.show.progress.title')}
        style={{ marginBottom: '16px' }}
      >
        <Progress percent={waveProgress.percent} status="active" />
        <div style={{ marginTop: 8 }}>
          {t('shared.picking_waves.show.progress.picked_count', {
            picked: waveProgress.pickedQty,
            total: waveProgress.totalQty,
          })}
        </div>
        <div style={{ color: '#888', fontSize: 12 }}>
          {t('shared.picking_waves.show.progress.pl_status_summary', waveProgress.buckets)}
        </div>
      </Card>

      {/* PickingLists */}
      <Card
        title={t('shared.picking_waves.show.picking_lists.title')}
        style={{ marginBottom: '16px' }}
      >
        <Table
          columns={[
            {
              title: t('models.picking_list.name'),
              dataIndex: 'display_name',
              key: 'name',
              render: (name, record) => (
                <Link to={`/picking_lists/${record.id}`}>{name}</Link>
              ),
            },
            {
              title: t('models.picking_list.status'),
              dataIndex: 'status',
              key: 'status',
              width: 120,
              render: (status) => (
                <Tag color={getStatusColor(status)}>
                  {t(`enums.picking_list.status.${status}`)}
                </Tag>
              ),
            },
            {
              title: t('shared.picking_waves.show.picking_lists.progress'),
              key: 'progress',
              width: 180,
              render: (_, record) => {
                const total = record.total_quantity || 0
                const picked = record.picked_quantity || 0
                const rawPercent = total > 0 ? Math.round((picked / total) * 100) : 0
                const percent = Math.min(100, rawPercent) // 過撿（picked > total）防護
                return (
                  <Progress
                    percent={percent}
                    size="small"
                    format={() => `${picked} / ${total}`}
                  />
                )
              },
            },
            {
              title: t('shared.picking_waves.show.picking_lists.residual'),
              key: 'residual',
              width: 110,
              render: (_, record) => {
                const qty = record.tote_residual_quantity || 0
                if (qty === 0) return <span style={{ color: '#bbb' }}>-</span>
                return (
                  <Tag color="red">
                    {t('shared.picking_waves.show.picking_lists.residual_units', { count: qty })}
                  </Tag>
                )
              },
            },
          ]}
          dataSource={wave.picking_lists || []}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>

      {/* Fulfillments */}
      <Card
        id="fulfillments"
        title={t('shared.picking_waves.show.fulfillments.title')}
        extra={
          selectedReturnableCount > 0 && (
            <Button
              danger
              icon={<RollbackOutlined />}
              onClick={handleBatchReturnClick}
            >
              {t('shared.picking_waves.show.actions.batch_return', { count: selectedReturnableCount })}
            </Button>
          )
        }
      >
        <Table
          columns={fulfillmentColumns}
          dataSource={allFulfillments}
          rowKey="id"
          pagination={false}
          size="small"
          rowClassName={(record) =>
            record.order && highlightOrderIds.has(record.order.id) ? 'picking-wave-held-fl-row' : ''
          }
        />
      </Card>

      {/* 附件（彙整本波次所揀訂單的附件，唯讀；下載按鈕在頁面上方 action bar） */}
      <Card
        title={t('shared.picking_waves.show.attachments.title')}
        style={{ marginBottom: '16px' }}
      >
        <WaveAttachments attachments={wave.attachments || []} />
      </Card>

      {/* 列印揀貨單 Modal */}
      <Modal
        title={t('shared.picking_waves.show.print_modal.title')}
        open={printModalOpen}
        onCancel={() => setPrintModalOpen(false)}
        confirmLoading={printing}
        onOk={handlePrintSelected}
        okText={t('shared.picking_waves.show.print_modal.confirm')}
        okButtonProps={{ disabled: selectedPickingLists.size === 0 }}
        cancelText={t('common.button.cancel')}
      >
        <div style={{ marginBottom: 8 }}>
          <Checkbox
            checked={selectedPickingLists.size === wave.picking_lists?.length}
            indeterminate={selectedPickingLists.size > 0 && selectedPickingLists.size < wave.picking_lists?.length}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedPickingLists(new Set(wave.picking_lists.map(pl => pl.id)))
              } else {
                setSelectedPickingLists(new Set())
              }
            }}
          >
            {t('common.select_all')}
          </Checkbox>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingLeft: 24 }}>
          {wave.picking_lists?.map(pl => (
            <Checkbox
              key={pl.id}
              checked={selectedPickingLists.has(pl.id)}
              onChange={() => {
                const newSelected = new Set(selectedPickingLists)
                if (newSelected.has(pl.id)) {
                  newSelected.delete(pl.id)
                } else {
                  newSelected.add(pl.id)
                }
                setSelectedPickingLists(newSelected)
              }}
            >
              <Space>
                {pl.display_name}
                <Tag color={getStatusColor(pl.status)}>
                  {t(`enums.picking_list.status.${pl.status}`)}
                </Tag>
                <Typography.Text type="secondary">
                  {pl.picked_items_count || 0} / {pl.items_count || 0}
                </Typography.Text>
              </Space>
            </Checkbox>
          ))}
        </div>
      </Modal>

      {/* 退回 Fulfillment Modal（單筆）*/}
      <Modal
        title={t('shared.picking_waves.show.return_modal.title')}
        open={returnModalOpen}
        onCancel={() => {
          setReturnModalOpen(false)
          setReturnFulfillment(null)
          setReturnToteItems([])
          setReturnPutBackItems([])
        }}
        confirmLoading={returnLoading}
        onOk={handleReturnConfirm}
        okType="danger"
        okText={t('shared.picking_waves.show.return_modal.confirm')}
        cancelText={t('common.button.cancel')}
        width={700}
      >
        {returnLoading && returnToteItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <>
            <Alert
              type="warning"
              showIcon
              message={t('shared.picking_waves.show.return_modal.warning')}
              description={returnToteItems.length > 0
                ? t('shared.picking_waves.show.return_modal.has_put_back_required')
                : t('shared.picking_waves.show.return_modal.no_tote_inventory')
              }
              style={{ marginBottom: 16 }}
            />

            {returnToteItems.length > 0 && (
              <Table
                size="small"
                pagination={false}
                dataSource={returnToteItems}
                rowKey="inventory_id"
                columns={[
                  {
                    title: t('models.product.sku'),
                    key: 'sku',
                    width: 120,
                    render: (_, record) => (
                      <div>
                        <div>{record.product_sku || '-'}</div>
                        <div style={{ fontSize: 11, color: '#999' }}>{record.product_name}</div>
                      </div>
                    ),
                  },
                  {
                    title: t('shared.picking_waves.show.return_modal.put_back_required'),
                    dataIndex: 'put_back_required',
                    key: 'put_back_required',
                    width: 80,
                  },
                  {
                    title: t('shared.picking_waves.show.return_modal.target_shelf'),
                    key: 'target_shelf',
                    width: 200,
                    render: (_, record, index) => {
                      const options = record.source_shelves?.map(s => ({
                        value: s.shelf_id,
                        label: `${s.location} (${t('shared.picking_waves.show.return_modal.picked_from')})`,
                      })) || []

                      return (
                        <Select
                          style={{ width: '100%' }}
                          value={returnPutBackItems[index]?.shelf_id}
                          onChange={(val) => updateReturnShelf(index, val)}
                          options={options}
                          placeholder={t('shared.picking_waves.show.return_modal.select_shelf')}
                        />
                      )
                    },
                  },
                ]}
              />
            )}
          </>
        )}
      </Modal>

      {/* 批次退回 Fulfillment Modal */}
      <Modal
        title={t('shared.picking_waves.show.batch_return_modal.title')}
        open={batchReturnModalOpen}
        onCancel={closeBatchReturnModal}
        confirmLoading={batchReturnSubmitting}
        onOk={handleBatchReturnConfirm}
        okType="danger"
        okText={t('shared.picking_waves.show.batch_return_modal.confirm')}
        okButtonProps={{ disabled: batchReturnLoading || batchReturnEntries.length === 0 }}
        cancelText={t('common.button.cancel')}
        width={800}
      >
        {batchReturnLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
            <div style={{ marginTop: 8, color: '#999' }}>
              {t('shared.picking_waves.show.batch_return_modal.loading')}
            </div>
          </div>
        ) : batchReturnFetchError ? (
          <Alert type="error" showIcon message={batchReturnFetchError} />
        ) : batchReturnEntries.length === 0 ? (
          <Empty description={t('shared.picking_waves.show.batch_return_modal.empty')} />
        ) : (
          <>
            <Alert
              type="warning"
              showIcon
              message={t('shared.picking_waves.show.batch_return_modal.warning', { count: batchReturnEntries.length })}
              description={t('shared.picking_waves.show.batch_return_modal.description')}
              style={{ marginBottom: 16 }}
            />
            <Collapse
              defaultActiveKey={batchReturnEntries.map((e) => e.fulfillment.id)}
              items={batchReturnEntries.map((entry) => {
                const f = entry.fulfillment
                const hasPutBack = entry.toteItems.length > 0
                const orderName = f.order?.name || '-'
                return {
                  key: f.id,
                  label: (
                    <Space>
                      <Tag>{f.slot_number || '-'}</Tag>
                      <span>{orderName}</span>
                      <Typography.Text type="secondary">
                        {hasPutBack
                          ? t('shared.picking_waves.show.batch_return_modal.has_put_back', { count: entry.toteItems.length })
                          : t('shared.picking_waves.show.batch_return_modal.no_tote_inventory')}
                      </Typography.Text>
                    </Space>
                  ),
                  children: hasPutBack ? (
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={entry.toteItems}
                      rowKey="inventory_id"
                      columns={[
                        {
                          title: t('models.product.sku'),
                          key: 'sku',
                          width: 120,
                          render: (_, record) => (
                            <div>
                              <div>{record.product_sku || '-'}</div>
                              <div style={{ fontSize: 11, color: '#999' }}>{record.product_name}</div>
                            </div>
                          ),
                        },
                        {
                          title: t('shared.picking_waves.show.return_modal.put_back_required'),
                          dataIndex: 'put_back_required',
                          key: 'put_back_required',
                          width: 80,
                        },
                        {
                          title: t('shared.picking_waves.show.return_modal.target_shelf'),
                          key: 'target_shelf',
                          width: 220,
                          render: (_, record, index) => {
                            const options = record.source_shelves?.map((s) => ({
                              value: s.shelf_id,
                              label: `${s.location} (${t('shared.picking_waves.show.return_modal.picked_from')})`,
                            })) || []
                            return (
                              <Select
                                style={{ width: '100%' }}
                                value={entry.putBackItems[index]?.shelf_id}
                                onChange={(val) => updateBatchReturnShelf(f.id, index, val)}
                                options={options}
                                placeholder={t('shared.picking_waves.show.return_modal.select_shelf')}
                              />
                            )
                          },
                        },
                      ]}
                    />
                  ) : (
                    <Typography.Text type="secondary">
                      {t('shared.picking_waves.show.batch_return_modal.no_tote_detail')}
                    </Typography.Text>
                  ),
                }
              })}
            />
          </>
        )}
      </Modal>

      {/* 作廢 Wave Modal (§17) */}
      <Modal
        title={t('shared.picking_waves.show.abort_modal.title')}
        open={abortModalOpen}
        onCancel={closeAbortModal}
        confirmLoading={abortSubmitting}
        onOk={handleAbortConfirm}
        okType="danger"
        okText={t('shared.picking_waves.show.abort_modal.confirm')}
        okButtonProps={{ disabled: abortLoading || abortEntries.length === 0 || !abortConfirmed }}
        cancelText={t('common.button.cancel')}
        width={800}
      >
        {abortLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
            <div style={{ marginTop: 8, color: '#999' }}>
              {t('shared.picking_waves.show.abort_modal.loading')}
            </div>
          </div>
        ) : abortFetchError ? (
          <Alert type="error" showIcon message={abortFetchError} />
        ) : abortEntries.length === 0 ? (
          <Empty description={t('shared.picking_waves.show.abort_modal.empty')} />
        ) : (
          <>
            <Alert
              type="error"
              showIcon
              message={t('shared.picking_waves.show.abort_modal.warning', { count: abortEntries.length })}
              description={t('shared.picking_waves.show.abort_modal.description')}
              style={{ marginBottom: 16 }}
            />
            <Collapse
              defaultActiveKey={abortEntries.filter((e) => e.toteItems.length > 0).map((e) => e.fulfillment.id)}
              items={abortEntries.map((entry) => {
                const f = entry.fulfillment
                const hasPutBack = entry.toteItems.length > 0
                const orderName = f.order?.name || '-'
                return {
                  key: f.id,
                  label: (
                    <Space>
                      <Tag>{f.slot_number || '-'}</Tag>
                      <span>{orderName}</span>
                      <Typography.Text type="secondary">
                        {hasPutBack
                          ? t('shared.picking_waves.show.batch_return_modal.has_put_back', { count: entry.toteItems.length })
                          : t('shared.picking_waves.show.batch_return_modal.no_tote_inventory')}
                      </Typography.Text>
                    </Space>
                  ),
                  children: hasPutBack ? (
                    <Table
                      size="small"
                      pagination={false}
                      dataSource={entry.toteItems}
                      rowKey="inventory_id"
                      columns={[
                        {
                          title: t('models.product.sku'),
                          key: 'sku',
                          width: 120,
                          render: (_, record) => (
                            <div>
                              <div>{record.product_sku || '-'}</div>
                              <div style={{ fontSize: 11, color: '#999' }}>{record.product_name}</div>
                            </div>
                          ),
                        },
                        {
                          title: t('shared.picking_waves.show.return_modal.put_back_required'),
                          dataIndex: 'put_back_required',
                          key: 'put_back_required',
                          width: 80,
                        },
                        {
                          title: t('shared.picking_waves.show.return_modal.target_shelf'),
                          key: 'target_shelf',
                          width: 220,
                          render: (_, record, index) => {
                            const options = record.source_shelves?.map((s) => ({
                              value: s.shelf_id,
                              label: `${s.location} (${t('shared.picking_waves.show.return_modal.picked_from')})`,
                            })) || []
                            return (
                              <Select
                                style={{ width: '100%' }}
                                value={entry.putBackItems[index]?.shelf_id}
                                onChange={(val) => updateAbortShelf(f.id, index, val)}
                                options={options}
                                placeholder={t('shared.picking_waves.show.return_modal.select_shelf')}
                              />
                            )
                          },
                        },
                      ]}
                    />
                  ) : (
                    <Typography.Text type="secondary">
                      {t('shared.picking_waves.show.batch_return_modal.no_tote_detail')}
                    </Typography.Text>
                  ),
                }
              })}
            />
            <div style={{ marginTop: 16 }}>
              <Typography.Text strong>
                {t('shared.picking_waves.show.abort_modal.confirm_prompt', { name: wave?.name })}
              </Typography.Text>
              <Input
                style={{ marginTop: 8 }}
                value={abortConfirmText}
                onChange={(e) => setAbortConfirmText(e.target.value)}
                placeholder={wave?.name || ''}
                disabled={abortSubmitting}
              />
            </div>
          </>
        )}
      </Modal>

      {/* 裝箱單 (TO Label) Modal */}
      <Modal
        title={t('shared.picking_waves.show.to_label.modal_title')}
        open={toLabelModalOpen}
        onCancel={() => setToLabelModalOpen(false)}
        onOk={handleDownloadToLabel}
        confirmLoading={toLabelLoading}
        okText={t('shared.picking_waves.show.to_label.download')}
        okButtonProps={{ disabled: toLabelNorth <= 0 && toLabelSouth <= 0 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 80 }}>{t('shared.picking_waves.show.to_label.north')}：</span>
            <InputNumber min={0} max={20} value={toLabelNorth} onChange={(v) => setToLabelNorth(v || 0)} />
            <span>{t('shared.picking_waves.show.to_label.unit')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 80 }}>{t('shared.picking_waves.show.to_label.south')}：</span>
            <InputNumber min={0} max={20} value={toLabelSouth} onChange={(v) => setToLabelSouth(v || 0)} />
            <span>{t('shared.picking_waves.show.to_label.unit')}</span>
          </div>
        </div>
      </Modal>

    </div>
  )
}
